import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, deleteDoc, updateDoc, collection, query, where, limit, getDocs, onSnapshot } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigation } from './NavigationContext';
import { Platform } from 'react-native';
import Constants from 'expo-constants';
import { authService } from '../services/authService';
import { defaultPreferences } from '../constants/defaultPreferences';


const AuthContext = createContext();

// ── Admin preview mode ────────────────────────────────────────────────────────
// Pure client-side override: lets admins simulate any subscription tier for
// demos and QA without touching Firestore. Persisted in sessionStorage so it
// survives page refreshes within the same browser tab.
export const ADMIN_PREVIEW_PLANS = {
  FREE:     { planType: 'FREE',     subscriptionStatus: 'free',   queryLimit: 10,  backgroundQueryLimit: 1, maxSportsbooks: 1, maxSGPLegs: 0, maxCombos: 0 },
  BASIC:    { planType: 'BASIC',    subscriptionStatus: 'active', queryLimit: 100, backgroundQueryLimit: 2, maxSportsbooks: 2, maxSGPLegs: 2, maxCombos: 5 },
  PRO:      { planType: 'PRO',      subscriptionStatus: 'active', queryLimit: 250, backgroundQueryLimit: 3, maxSportsbooks: 4, maxSGPLegs: 3, maxCombos: 10 },
  ULTIMATE: { planType: 'ULTIMATE', subscriptionStatus: 'active', queryLimit: -1,  backgroundQueryLimit: 5, maxSportsbooks: 99, maxSGPLegs: 6, maxCombos: 50 },
};

const _PREVIEW_KEY = 'admin_preview_plan';
function _loadPreviewPlan() {
  try { return sessionStorage.getItem(_PREVIEW_KEY) || null; } catch { return null; }
}
function _savePreviewPlan(plan) {
  try {
    if (plan) sessionStorage.setItem(_PREVIEW_KEY, plan);
    else sessionStorage.removeItem(_PREVIEW_KEY);
  } catch {}
}

// Module-level ref prevents duplicate onAuthStateChanged subscriptions
// (React 18 Strict Mode double-invokes effects; Firestore throws "Target ID exists" if
//  a new snapshot is opened before the previous one fully tears down)
let _authUnsub = null;

// Module-level singleton onSnapshot listener for the user document.
// Using onSnapshot (persistent listener) instead of getDoc (temporary target) avoids
// Target ID recycling races in Firestore SDK with memoryLocalCache: a temporary target
// gets a recycled ID that may still be claimed by another just-released target.
// A persistent onSnapshot registers exactly ONE target per session, shared across
// all React Strict Mode remounts via Promise-based handoff.
let _userDocListenerUid = null;   // uid the current listener is for
let _userDocUnsub = null;         // current onSnapshot unsubscribe fn
let _userDocPending = null;       // { resolve, reject } for the first snapshot
let _userDocPromise = null;       // the Promise that resolves on first snapshot

function _tryRegisterUserDoc(uid, resolve, reject, attempt) {
  const _isTargetIdErr = (e) =>
    e?.code === 'already-exists' || (e?.message || '').includes('Target ID already exists');

  const db = getFirestore();
  const ref = doc(db, 'users', uid);

  const onError = (err) => {
    _userDocUnsub = null;
    _userDocListenerUid = null;
    _userDocPromise = null;
    _userDocPending = null;
    if (_isTargetIdErr(err) && attempt < 8) {
      // Back off and retry — the competing target will be released shortly.
      setTimeout(() => _tryRegisterUserDoc(uid, resolve, reject, attempt + 1), 200 * (attempt + 1));
    } else {
      reject(err);
    }
  };

  try {
    _userDocUnsub = onSnapshot(ref, (snap) => {
      if (_userDocPending) {
        const pending = _userDocPending;
        _userDocPending = null;
        pending.resolve(snap);
      }
    }, onError);
  } catch (err) {
    _userDocUnsub = null;
    _userDocListenerUid = null;
    _userDocPromise = null;
    _userDocPending = null;
    if (_isTargetIdErr(err) && attempt < 8) {
      setTimeout(() => _tryRegisterUserDoc(uid, resolve, reject, attempt + 1), 200 * (attempt + 1));
    } else {
      reject(err);
    }
  }
}

function getUserDocOnce(uid) {
  // Return the existing promise if we already have an active listener for this uid.
  if (_userDocListenerUid === uid && _userDocPromise) return _userDocPromise;

  // Tear down any listener for a different uid.
  if (_userDocUnsub) {
    try { _userDocUnsub(); } catch {}
    _userDocUnsub = null;
    _userDocListenerUid = null;
    _userDocPending = null;
    _userDocPromise = null;
  }

  _userDocListenerUid = uid;
  _userDocPromise = new Promise((resolve, reject) => {
    _userDocPending = { resolve, reject };
    _tryRegisterUserDoc(uid, resolve, reject, 0);
  });
  return _userDocPromise;
}

// Keep a module-level onSnapshot for site_config/onboarding (guard prevents double-fire).
const _activeReads = new Map(); // key → { promise, expiresAt }
const _DEDUP_TTL_MS = 2000;
function deduplicatedGetDoc(docRef) {
  const key = docRef.path;
  const cached = _activeReads.get(key);
  if (cached && Date.now() < cached.expiresAt) return cached.promise;
  const promise = getDoc(docRef);
  // Clear on rejection so retries make a fresh Firestore call.
  promise.catch(() => { _activeReads.delete(key); });
  _activeReads.set(key, { promise, expiresAt: Date.now() + _DEDUP_TTL_MS });
  return promise;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminPreviewPlan, setAdminPreviewPlanState] = useState(_loadPreviewPlan);
  const [profileLoading, setProfileLoading] = useState(false);
  const { navigateTo, currentScreen, setUserTypeFromAuth } = useNavigation();
  const maxRetries = 3;
  const retryDelay = 1000; // 1 second
  const lastUidRef = useRef(null);
  const didNavigateRef = useRef(false);
  const onboardingEnabledRef = useRef(true); // tracks site_config/onboarding.enabled
  const onboardingFetchedRef = useRef(false); // prevents Strict Mode double-fire

  // Keep stable refs to context callbacks so the auth listener effect
  // doesn't need to re-subscribe every time NavigationProvider re-renders.
  const navigateToRef = useRef(navigateTo);
  const currentScreenRef = useRef(currentScreen);
  const setUserTypeFromAuthRef = useRef(setUserTypeFromAuth);
  useEffect(() => { navigateToRef.current = navigateTo; }, [navigateTo]);
  useEffect(() => { currentScreenRef.current = currentScreen; }, [currentScreen]);
  useEffect(() => { setUserTypeFromAuthRef.current = setUserTypeFromAuth; }, [setUserTypeFromAuth]);

  // Read onboarding enabled flag once on mount (no real-time listener needed)
  useEffect(() => {
    // useRef guard: survives Strict Mode's unmount/remount cycle so the second
    // invocation is skipped entirely, preventing a duplicate Firestore Target ID.
    if (onboardingFetchedRef.current) return;
    onboardingFetchedRef.current = true;
    const db = getFirestore();
    deduplicatedGetDoc(doc(db, 'site_config', 'onboarding')).then(snap => {
      const d = snap.exists() ? snap.data() : {};
      onboardingEnabledRef.current = d.enabled !== false;
    }).catch(() => {}); // silently ignore permission errors
  }, []);
  // Dev mode auto-login disabled - users must login manually
  const [devModeUser, setDevModeUser] = useState(null);
  const isDev = process.env.NODE_ENV === 'development';


  const register = async (email, password, username) => {
    const { user, error } = await authService.register(email, password, username);

    return {user, error};
  };

  const signIn = async (email, password) => {
    const { user, error } = await authService.login(email, password);
    if (user) {
      setUser(user);
    }
    return {user, error};
  };

  const signInWithGoogleAuth = async () => {
    const { user, error } = await authService.signInWithGoogle();
    if (user) {
      setUser(user);
    }
    return { user, error };
  };

  const logout = async () => {
    const { error } = await authService.logout();
    setUser(null);
    lastUidRef.current = null;
    didNavigateRef.current = false;
    return { error };
  };

  const resetPassword = async (email) => {
    try {
      // Public, rate-limited reset endpoint (no auth required, no enumeration)
      const response = await fetch('https://us-central1-bet-optimized-fe922.cloudfunctions.net/requestPasswordReset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send reset email');
      }

      return { success: true };
    } catch (error) {
      console.error('Error sending password reset email:', error);
      return { success: false, error: error.message };
    }
  };

  useEffect(() => {
    const auth = getAuth();
    let unsubscribed = false;

    // Tear down any previous subscription before registering a new one
    if (_authUnsub) {
      _authUnsub();
      _authUnsub = null;
    }
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (unsubscribed) return;
      
      try {
        if (firebaseUser) {
          const db = getFirestore();
          
          // Use a module-level onSnapshot singleton for the user doc.
          // This registers exactly ONE persistent Firestore target per session,
          // shared across React Strict Mode remounts — eliminating Target ID
          // recycling races that plagued getDoc-based approaches.
          const handleUserData = async () => {
            if (unsubscribed) return;
            try {
              const userDoc = await getUserDocOnce(firebaseUser.uid);
              if (unsubscribed) return;

              let userData = null;
              const userRef = doc(db, 'users', firebaseUser.uid);

              if (userDoc.exists()) {
                userData = userDoc.data();
                // Update last login timestamp (best-effort)
                try {
                  await updateDoc(userRef, { lastLogin: new Date() });
                } catch (updateError) {
                  console.warn('Could not update last login time:', updateError);
                }
              } else {
                // Adopt legacy admin-created record keyed by email into uid doc on first login
                try {
                  const emailKey = (firebaseUser.email || '').toLowerCase();
                  if (emailKey) {
                    const legacyRef = doc(db, 'users', emailKey);
                    const legacySnap = await getDoc(legacyRef);
                    if (legacySnap.exists()) {
                      const legacyData = legacySnap.data();
                      const merged = { ...legacyData, uid: firebaseUser.uid, email: firebaseUser.email };
                      if (!unsubscribed) await setDoc(userRef, merged, { merge: true });
                      if (unsubscribed) return;
                      userData = merged;
                    }
                  }
                } catch (adoptErr) {
                  console.warn('User adopt by email failed:', adoptErr);
                }
              }

              const mergedUser = userData ? { ...userData, uid: firebaseUser.uid } : {
                uid: firebaseUser.uid,
                displayName: firebaseUser.displayName || '',
                email: firebaseUser.email || '',
                phoneNumber: firebaseUser.phoneNumber || '',
                photoURL: firebaseUser.photoURL || ''
              };

              setUser(mergedUser);

              if (userData) {
                setUserProfile({
                  ...userData,
                  preferences: { ...defaultPreferences, ...(userData.preferences || {}) }
                });
              }

              if (!unsubscribed) {
                setUserTypeFromAuthRef.current?.({
                  userType: userData?.userType || 'user',
                  alphaAccess: !!userData?.alphaAccess,
                  betaAccess: !!userData?.betaAccess,
                  success: true,
                });
              }

              if (userData?.suspended) {
                navigateToRef.current('Suspended');
                didNavigateRef.current = true;
              } else if (lastUidRef.current !== firebaseUser.uid && !didNavigateRef.current) {
                const contentScreens = ['SgpFlow', 'SameGameParlay', 'Explore', 'Profile', 'Admin', 'Plans', 'Onboarding', 'ForgotPassword', 'ResetPassword', 'Login', 'Register'];
                if (!contentScreens.includes(currentScreenRef.current)) {
                  const needsOnboarding = !userData?.onboardingCompleted && !userData?.onboardingSkipped;
                  const onboardingLive = onboardingEnabledRef.current;
                  navigateToRef.current(needsOnboarding && onboardingLive ? 'Onboarding' : 'Home');
                }
                didNavigateRef.current = true;
              }
              lastUidRef.current = firebaseUser.uid;
            } catch (error) {
              console.warn('[AuthContext] Error loading user document:', error.message || error);
              if (!unsubscribed) {
                const basicUser = {
                  uid: firebaseUser.uid,
                  displayName: firebaseUser.displayName || '',
                  email: firebaseUser.email || '',
                  phoneNumber: firebaseUser.phoneNumber || '',
                  photoURL: firebaseUser.photoURL || ''
                };
                setUser(basicUser);
                setUserProfile({ ...basicUser, preferences: defaultPreferences });
                lastUidRef.current = firebaseUser.uid;
                setUserTypeFromAuthRef.current?.({
                  userType: 'user',
                  alphaAccess: false,
                  betaAccess: false,
                  success: false,
                });
              }
            }
          };

          await handleUserData();
        } else {
          setUser(null);
          lastUidRef.current = null;
          didNavigateRef.current = false;
          // Tear down the user-doc onSnapshot singleton on logout.
          if (_userDocUnsub) {
            try { _userDocUnsub(); } catch {}
            _userDocUnsub = null;
            _userDocListenerUid = null;
            _userDocPending = null;
            _userDocPromise = null;
          }
        }
      } catch (error) {
        console.error('AuthContext: Error in auth state change:', error);
        if (isDev) {
          console.warn('Development mode: Continuing despite error');
        } else {
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    });

    _authUnsub = unsubscribe;

    return () => {
      unsubscribed = true;
      unsubscribe();
      if (_authUnsub === unsubscribe) _authUnsub = null;
    };
  }, []); // Empty deps: register once. navigateTo/currentScreen accessed via refs.

  // Dev mode auto-login (DISABLED - manual login required)
  // This was causing unintended auto-login behavior
  // useEffect(() => {
  //   const handleDevModeAutoLogin = async () => {
  //     if (isDev && devModeUser) {
  //       try {
  //         const auth = getAuth();
  //         const password = process.env.REACT_APP_DEV_PASSWORD;
  //         if (!password) {
  //           throw new Error('Development password not found');
  //         }

  //         if (!auth.currentUser || auth.currentUser.email !== devModeUser) {
  //           await signInWithEmailAndPassword(auth, devModeUser, password);
  //         }
  //       } catch (error) {
  //         console.error('Dev mode auto-login error:', error);
  //         localStorage.removeItem('devModeUser');
  //         setDevModeUser(null);
  //       }
  //     }
  //   };

  //   handleDevModeAutoLogin();
  // }, [devModeUser, isDev]);

  // Store devModeUser in localStorage (DISABLED)
  // useEffect(() => {
  //   if (isDev) {
  //     if (devModeUser) {
  //       localStorage.setItem('devModeUser', devModeUser);
  //     } else {
  //       localStorage.removeItem('devModeUser');
  //     }
  //   }
  // }, [devModeUser, isDev]);
  
  // Clear any existing devModeUser from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const storedDevUser = localStorage.getItem('devModeUser');
      if (storedDevUser) {
        console.log('Clearing stored dev mode user');
        localStorage.removeItem('devModeUser');
      }
    }
  }, []);

  // Load user profile when user changes
  useEffect(() => {
    if (user?.uid) {
      loadUserProfile();
    } else {
      setUserProfile(null);
    }
  }, [user?.uid]);

  // Load user profile from Firestore
  const loadUserProfile = async () => {
    if (!user?.uid) return;

    // handleUserData already populated userProfile from the same Firestore read —
    // skip the redundant getDoc to avoid a concurrent Target ID collision with
    // NavigationContext's onSnapshot for the same document.
    if (userProfile?.uid === user.uid) {
      setProfileLoading(false);
      return;
    }
    
    setProfileLoading(true);
    
    try {
      const db = getFirestore();
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await deduplicatedGetDoc(userRef);
      
      if (userSnap.exists()) {
        // Merge with default preferences if some are missing
        const profileData = userSnap.data();
        const userProfileWithDefaults = {
          ...profileData,
          preferences: {
            ...defaultPreferences,
            ...profileData.preferences
          }
        };
        setUserProfile(userProfileWithDefaults);
      } else {
        // If no user profile exists, create with defaults
        const newUserProfile = {
          uid: user.uid,
          username: user.displayName || '',
          email: user.email || '',
          phoneNumber: user.phoneNumber || '',
          profilePhoto: user.photoURL || '',
          preferences: defaultPreferences
        };
        setUserProfile(newUserProfile);
        // Save the new profile to Firestore
        await setDoc(userRef, newUserProfile, { merge: true });
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  // Save user profile to Firestore
  const saveUserProfile = async (updatedProfile) => {
    if (!user?.uid) {
      return { success: false, error: 'No user ID found' };
    }

    setProfileLoading(true);

    try {
      const db = getFirestore();
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, updatedProfile, { merge: true });
      
      setUserProfile(prev => ({ ...prev, ...updatedProfile }));
      return { success: true };
    } catch (error) {
      console.error('Error saving user profile:', error);
      return { success: false, error: error.message };
    } finally {
      setProfileLoading(false);
    }
  };

  // Upload profile photo to Firebase Storage (rebuilt for reliability)
  const uploadProfilePhoto = async (fileBlob, fileName) => {
    if (!user?.uid) {
      console.error('[uploadProfilePhoto] No user ID');
      return { success: false, error: 'No user ID found' };
    }

    console.log('[uploadProfilePhoto] Starting upload for user:', user.uid);
    setProfileLoading(true);
    
    try {
      if (Platform.OS === 'web') {
        window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'info', text: 'Uploading photo…' } }));
      }
    } catch {}

    try {
      // Step 1: Compress image if too large (max 1MB, 800px width)
      let processedBlob = fileBlob;
      const maxSize = 1024 * 1024; // 1MB
      
      if (fileBlob.size > maxSize) {
        console.log('[uploadProfilePhoto] Compressing image from', fileBlob.size, 'bytes');
        try {
          const compressImage = (blob, maxWidth = 800) => new Promise((resolve, reject) => {
            const img = new Image();
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            img.onerror = () => reject(new Error('Image load error'));
            img.onload = () => {
              try {
                let { width, height } = img;
                if (width > maxWidth) {
                  height = (height * maxWidth) / width;
                  width = maxWidth;
                }
                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);
                canvas.toBlob(
                  (compressed) => resolve(compressed || blob),
                  'image/jpeg',
                  0.85
                );
              } catch (err) {
                reject(err);
              }
            };
            img.src = URL.createObjectURL(blob);
          });
          
          processedBlob = await compressImage(fileBlob);
          console.log('[uploadProfilePhoto] Compressed to', processedBlob.size, 'bytes');
        } catch (compErr) {
          console.warn('[uploadProfilePhoto] Compression failed, using original:', compErr);
        }
      }

      // Step 2: Convert to base64
      const toBase64 = (blob) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = String(reader.result || '');
          resolve(result);
        };
        reader.onerror = () => reject(new Error('FileReader error'));
        reader.readAsDataURL(blob);
      });
      
      const dataUrl = await toBase64(processedBlob);
      console.log('[uploadProfilePhoto] Converted to base64, length:', dataUrl.length);

      // Step 3: Upload to server-side Firebase Storage endpoint
      const base = (
        (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
        || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
        || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
        || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
      );
      
      if (!base) {
        throw new Error('Functions base URL not configured');
      }

      console.log('[uploadProfilePhoto] Uploading to:', `${base}/uploadUserImage`);
      const uploadResponse = await fetch(`${base}/uploadUserImage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          base64: dataUrl,
          contentType: processedBlob.type || 'image/jpeg',
          filename: `profile_${user.uid}_${Date.now()}.jpg`
        })
      });

      const responseText = await uploadResponse.text();
      console.log('[uploadProfilePhoto] Upload response status:', uploadResponse.status);

      let publicURL = '';
      if (uploadResponse.ok) {
        const responseData = JSON.parse(responseText);
        publicURL = responseData.url;
        console.log('[uploadProfilePhoto] Got public URL:', publicURL);
      } else {
        console.error('[uploadProfilePhoto] Upload failed:', responseText);
        throw new Error(`Upload failed: ${responseText}`);
      }

      // Step 4: Save URL to Firestore user document
      const db = getFirestore();
      const userRef = doc(db, 'users', user.uid);
      
      await setDoc(userRef, {
        profilePhoto: publicURL,
        photoURL: publicURL, // Store in both fields for compatibility
        updatedAt: new Date().toISOString()
      }, { merge: true });

      console.log('[uploadProfilePhoto] Saved to Firestore');

      // Step 5: Update all local state immediately
      setUser((prev) => prev ? { ...prev, profilePhoto: publicURL, photoURL: publicURL } : prev);
      setUserProfile((prev) => prev ? { ...prev, profilePhoto: publicURL, photoURL: publicURL } : prev);

      // Step 6: Reload profile from Firestore to ensure consistency
      await loadUserProfile();

      // Step 7: Show success toast
      if (Platform.OS === 'web') {
        window.dispatchEvent(new CustomEvent('toast', {
          detail: { type: 'success', text: 'Profile photo updated!' }
        }));
      }

      console.log('[uploadProfilePhoto] Upload complete');
      return { success: true, photoURL: publicURL };

    } catch (error) {
      console.error('[uploadProfilePhoto] Error:', error);
      
      if (Platform.OS === 'web') {
        window.dispatchEvent(new CustomEvent('toast', {
          detail: { type: 'error', text: `Upload failed: ${error.message}` }
        }));
      }
      
      return { success: false, error: error.message };
    } finally {
      setProfileLoading(false);
    }
  };

  // Remove profile photo (rebuilt)
  const removeProfilePhoto = async () => {
    if (!user?.uid) {
      console.error('[removeProfilePhoto] No user ID');
      return { success: false, error: 'No user ID found' };
    }

    console.log('[removeProfilePhoto] Removing photo for user:', user.uid);
    setProfileLoading(true);
    
    try {
      if (Platform.OS === 'web') {
        window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'info', text: 'Removing photo…' } }));
      }
    } catch {}

    try {
      // Step 1: Clear from Firestore
      const db = getFirestore();
      const userRef = doc(db, 'users', user.uid);
      
      await setDoc(userRef, {
        profilePhoto: '',
        photoURL: '',
        updatedAt: new Date().toISOString()
      }, { merge: true });

      console.log('[removeProfilePhoto] Cleared from Firestore');

      // Step 2: Update all local state
      setUser((prev) => prev ? { ...prev, profilePhoto: '', photoURL: '' } : prev);
      setUserProfile((prev) => prev ? { ...prev, profilePhoto: '', photoURL: '' } : prev);

      // Step 3: Reload profile
      await loadUserProfile();

      // Step 4: Success toast
      if (Platform.OS === 'web') {
        window.dispatchEvent(new CustomEvent('toast', {
          detail: { type: 'success', text: 'Profile photo removed' }
        }));
      }

      console.log('[removeProfilePhoto] Complete');
      return { success: true };

    } catch (error) {
      console.error('[removeProfilePhoto] Error:', error);
      
      if (Platform.OS === 'web') {
        window.dispatchEvent(new CustomEvent('toast', {
          detail: { type: 'error', text: `Failed to remove photo: ${error.message}` }
        }));
      }
      
      return { success: false, error: error.message };
    } finally {
      setProfileLoading(false);
    }
  };

  // Update user preferences
  const updateUserPreferences = async (preferences) => {
    if (!user?.uid) {
      return { success: false, error: 'No user ID found' };
    }

    const updatedProfile = { preferences };
    return await saveUserProfile(updatedProfile);
  };

  // ── Admin preview helpers ─────────────────────────────────────────────────
  const setAdminPreviewPlan = (planKey) => {
    const key = planKey && ADMIN_PREVIEW_PLANS[planKey] ? planKey : null;
    _savePreviewPlan(key);
    setAdminPreviewPlanState(key);
  };
  const clearAdminPreviewPlan = () => setAdminPreviewPlan(null);

  // effectiveUserProfile: real profile with subscription overridden when previewing.
  // Admin status is NEVER overridden — userType, alphaAccess, betaAccess stay real.
  const effectiveUserProfile = React.useMemo(() => {
    if (!userProfile || !adminPreviewPlan) return userProfile;
    const previewSub = ADMIN_PREVIEW_PLANS[adminPreviewPlan];
    return {
      ...userProfile,
      // Keep real userType/admin status — never override
      subscription: {
        ...(userProfile.subscription || {}),
        ...previewSub,
        _isPreview: true,
      },
    };
  }, [userProfile, adminPreviewPlan]);

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      loading,
      devModeUser,
      setDevModeUser,
      logout,
      register,
      signIn,
      signInWithGoogleAuth,
      resetPassword,
      // User profile management
      userProfile,
      effectiveUserProfile,
      profileLoading,
      saveUserProfile,
      uploadProfilePhoto,
      removeProfilePhoto,
      updateUserPreferences,
      loadUserProfile,
      defaultPreferences,
      // Admin preview mode
      adminPreviewPlan,
      setAdminPreviewPlan,
      clearAdminPreviewPlan,
      ADMIN_PREVIEW_PLANS,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context;
}

